// Повторный вход поверх редактора, когда сессия закончилась посреди работы (срок, выход в другой вкладке,
// администратор завершил сеансы или заблокировал учётную запись). Несохранённое уже лежит копией в браузере
// (onSessionLost), а запросы, получившие 401, ждут входа и повторяются сами - текст не теряется.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../ui/icons';
import { appStore } from '../../core/store';
import { setApiToken } from '../../core/api';
import { authApi, relogged, useAuth, type Me } from './session';
import { Check, ErrorBox, errorOf, PasswordField, passwordChecks, Rules } from './fields';

// почему сессия закончилась (поле reason ответа 401 сервера)
const REASONS: Record<string, string> = {
  expired: 'Срок входа истёк.',
  logout: 'Вы вышли из редактора в другой вкладке или окне.',
  relogin: 'В этом браузере выполнен вход под другой учётной записью.',
  others: 'Вы завершили этот сеанс с другого устройства («Выйти на других устройствах»).',
  password: 'Пароль учётной записи изменён - войдите с новым паролем.',
  password_reset: 'Администратор сбросил ваш пароль: получите у него временный пароль и войдите с ним.',
  admin: 'Администратор завершил ваши сеансы.',
  role: 'Администратор изменил права вашей учётной записи - войдите снова.',
  blocked: 'Учётная запись заблокирована администратором.',
  deleted: 'Учётная запись удалена администратором. Если это ошибка - обратитесь к администратору.',
};

// последнее нажатие клавиши: окно входа не забирает фокус посреди набора текста (иначе буквы уйдут в поле пароля)
let lastKeyAt = 0;
if (typeof window !== 'undefined') window.addEventListener('keydown', () => { lastKeyAt = Date.now(); }, true);

export function Reauth() {
  const lost = useAuth((s) => s.lost);
  const me = useAuth((s) => s.me);
  const [pw, setPw] = useState('');
  const [remember, setRemember] = useState(!!me?.session.remember);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; kind: 'error' | 'wait' }>({ text: '', kind: 'error' });
  // временный пароль (администратор сбросил пароль): после входа - сразу смена
  const [temp, setTemp] = useState<{ me: Me; old: string } | null>(null);
  const [n1, setN1] = useState('');
  const [n2, setN2] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => { if (lost) { setPw(''); setErr({ text: '', kind: 'error' }); setTemp(null); } }, [lost?.at]);
  // фокус в поле пароля - только когда человек перестал печатать (1,5 с тишины)
  useEffect(() => {
    if (!lost) return;
    const t = window.setInterval(() => {
      if (Date.now() - lastKeyAt < 1500) return;
      window.clearInterval(t);
      const f = formRef.current?.querySelector('input[type=password], input[type=text]') as HTMLInputElement | null;
      if (f && !formRef.current?.contains(document.activeElement)) f.focus();
    }, 300);
    return () => window.clearInterval(t);
  }, [lost?.at]);
  if (!lost || !me) return null;
  const pid = appStore.getState().projectId;
  const saveState = appStore.getState().save.state;
  const conflict = saveState === 'conflict';
  const dirty = ['dirty', 'saving', 'error'].includes(saveState);
  const why = REASONS[lost.reason || ''] || 'Срок входа истёк, вы вышли в другой вкладке или администратор завершил ваши сеансы.';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr({ text: '', kind: 'error' });
    try {
      if (temp) {
        if (n1 !== n2) throw new Error('Пароли не совпадают');
        if (n1 === temp.old) throw new Error('Новый пароль должен отличаться от временного');
        relogged(await authApi.changePassword(temp.old, n1));
        return;
      }
      if (!pw) throw new Error('Введите пароль');
      const m = await authApi.login(me.user.login, pw, remember);
      if (m.user.id !== me.user.id) { location.reload(); return; }
      if (m.user.mustChangePassword) {
        // до смены пароля сервер не выполнит ни одного запроса: токен ставим, окно остаётся
        setTemp({ me: m, old: pw });
        setApiToken(m.csrf);
        return;
      }
      relogged(m);
    } catch (ex: any) { setErr(errorOf(ex)); } finally { setBusy(false); }
  };

  return createPortal(
    <div className="modal-back au-reauth-back">
      <form ref={formRef} className="modal au-reauth" role="dialog" aria-modal="true" aria-label="Повторный вход" data-testid="reauth" onSubmit={submit} noValidate>
        <div className="au-reauth-head">
          <span className="au-reauth-ico"><Icon name="lock" size={20} /></span>
          <div>
            <div className="au-reauth-title">{temp ? 'Смените временный пароль' : 'Сеанс завершён - войдите снова'}</div>
            <div className="au-reauth-sub" data-testid="reauth-reason">{temp ? 'Администратор сбросил ваш пароль. Придумайте новый - после этого работа продолжится.'
              : why}</div>
          </div>
        </div>
        <div className="modal-body">
          {pid && (
            <div className={'au-notice' + (dirty || conflict ? ' au-notice-ok' : '')} data-testid="reauth-draft">
              <Icon name={conflict ? 'warn' : dirty ? 'save' : 'ok'} size={18} />
              <span>{conflict
                ? <>В отчёте <b>{pid.replace(/^u\d+:/, '')}</b> есть несохранённые правки (конфликт версий): копия - в этом браузере. После входа выберите на плашке над листом, какую версию оставить.</>
                : dirty
                ? <>Несохранённые правки отчёта <b>{pid.replace(/^u\d+:/, '')}</b> не потеряны: копия - в этом браузере, после входа они будут отправлены на сервер.</>
                : <>Отчёт <b>{pid.replace(/^u\d+:/, '')}</b> сохранён. После входа работа продолжится с того же места.</>}</span>
            </div>
          )}
          {!temp ? <>
            <div className="au-who"><span className="au-who-label">Учётная запись</span><b data-testid="reauth-login">{me.user.login}</b></div>
            <PasswordField label={lost.reason === 'password_reset' ? 'Временный пароль от администратора' : 'Пароль'} value={pw} onChange={setPw}
              testid="reauth-password" autoComplete="current-password" />
            <Check checked={remember} onChange={setRemember} label="Запомнить меня на этом устройстве" testid="reauth-remember" />
          </> : <>
            <PasswordField label="Новый пароль" value={n1} onChange={setN1} testid="reauth-new" autoComplete="new-password" autoFocus />
            <PasswordField label="Новый пароль ещё раз" value={n2} onChange={setN2} testid="reauth-new2" autoComplete="new-password" />
            <Rules items={passwordChecks(n1, n2, me.user.login)} />
          </>}
          <ErrorBox text={err.text} kind={err.kind} testid="reauth-error" />
        </div>
        <div className="modal-foot">
          <a className="au-link au-reauth-other" href="/login?logout=1" data-testid="reauth-other">Войти под другой учётной записью</a>
          <span className="topbar-spacer" />
          <button type="submit" className="btn btn-primary au-reauth-submit" disabled={busy} data-testid="reauth-submit">
            {busy && <Icon name="spinner" className="spin" size={14} />}{temp ? 'Сменить пароль' : 'Войти и продолжить'}
          </button>
        </div>
      </form>
    </div>, document.body);
}
